/**
 * Reading operations service.
 * Provides async functions for manual reading CRUD operations.
 */

import { queryClient as globalQueryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { ManualDMInputResponse } from "model/manualInputTypes";
import { manualInputApiV1 } from "model/readingsApiHelpers";

// Use the same API class pattern as useManualReadingsQueries
const manualInputApi = new API<ManualDMInputResponse>(
  "manualDryMatterInput",
  "v2"
);

/**
 * Prepare a reading request for the backend.
 * Transforms values to backend format (divides by 10000).
 * Transforms values to backend format.
 */
function prepareReadingRequest(
  manualInput: ManualDMInputResponse
): ManualDMInputResponse {
  return {
    guid: manualInput.guid,
    manualDryMatterInput: {
      ...manualInput.manualDryMatterInput,
      dryMatterValue:
        manualInput.manualDryMatterInput.dryMatterValue &&
        manualInput.manualDryMatterInput.dryMatterValue / 10000,
      dateTimeMeasurementStart:
        manualInput.manualDryMatterInput.dateTimeMeasurementStart,
      dateTimeMeasurementEnd:
        manualInput.manualDryMatterInput.dateTimeMeasurementEnd,
      event: {
        ...manualInput.manualDryMatterInput.event,
        postEventCover:
          manualInput.manualDryMatterInput.event.postEventCover &&
          manualInput.manualDryMatterInput.event.postEventCover / 10000,
        preEventCover:
          manualInput.manualDryMatterInput.event.preEventCover &&
          manualInput.manualDryMatterInput.event.preEventCover / 10000,
      },
    },
  };
}

/**
 * Invalidate readings cache for a field so event lists update.
 */
async function invalidateReadingsCache(fieldGuid: string) {
  await globalQueryClient.invalidateQueries({
    queryKey: queryKeys.events.readings.byField(fieldGuid),
  });
}

/**
 * Create a single reading.
 * Transforms values to backend format (divides by 10000).
 */
export async function createOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  const preparedInput = prepareReadingRequest(reading);
  const result = await manualInputApi.createOne(preparedInput);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(result.guid);
}

/**
 * Update a single reading.
 */
export async function updateOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  const preparedInput = prepareReadingRequest(reading);
  await manualInputApiV1.updateOne(preparedInput);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(reading.guid);
}

/**
 * Delete a single reading.
 */
export async function deleteOneReading(
  readingId: string,
  fieldGuid?: string
): Promise<void> {
  await manualInputApiV1.deleteOne(readingId);
  if (fieldGuid) {
    await invalidateReadingsCache(fieldGuid);
  }
}
